// /checkout/cobranca/:chargeId — tela pública pra pagar uma cobrança que já
// existe (avulsa ou mensalidade). 3 passos: Revisar → Pagamento → Pronto.
// Sem auth — protegida pelo UUID no link.
//
// O cliente escolhe Pix ou cartão. Cobrança ainda não emitida no Asaas nasce
// aqui; cobrança já emitida (a mensalidade do cron) tem o meio TROCADO no
// mesmo payment, preservando vencimento e dunning.
//
// VISUAL: kit do checkout público (`components/checkout-public/kit.tsx`) —
// mesmo creme, mesmo preto, mesma régua sob os títulos do CheckoutFlow. Até
// 2026-08-19 esta tela usava os tokens do painel ADMIN (escuros): o cliente
// saía de um checkout claro, clicava em pagar e caía numa tela preta que
// parecia outro produto, na hora exata de digitar o cartão. Em 2026-08-24 a
// cópia inline do visual virou o kit compartilhado.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Check,
  CheckCircle2,
  CreditCard,
  Loader2,
  Lock,
  QrCode,
  Receipt,
  ShoppingBag,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@control-center/ui';

import {
  CardInput,
  CheckoutErrorBox,
  CheckoutSection,
  CheckoutShell,
  CheckoutWaitingFooter,
  CopyCodeField,
} from '../../components/checkout-public/kit';
import {
  cardSaveText,
  fetchChargeCheckout,
  fetchChargeCheckoutStatus,
  payCharge,
  type ChargePayResult,
} from '../../lib/charge-checkout-api';
import { ReserveiChargeCheckout } from '../../components/checkout-reservei/ReserveiChargeCheckout';
import { mapAsaasError } from '../../lib/asaas-errors';

// BOLETO fora (regra do dono, 2026-08-19). É esta a tela que o cliente abre pra
// pagar a fatura, e é por ela que ele escolhe cartão — o único meio que deixa a
// mensalidade seguinte debitar sozinha. Oferecer boleto aqui é oferecer o único
// meio que garante pagamento manual pra sempre.
type Method = 'pix' | 'credit_card';
type Step = 'review' | 'payment' | 'result';

function formatBRL(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const onlyDigits = (v: string): string => v.replace(/\D/g, '');
const maskCard = (v: string): string =>
  onlyDigits(v)
    .slice(0, 16)
    .replace(/(.{4})/g, '$1 ')
    .trim();
const maskCep = (v: string): string => {
  const d = onlyDigits(v).slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
};

const METHODS: { value: Method; label: string; hint: string; icon: typeof QrCode }[] = [
  // Sem "renova sozinho": só renova quem marca a caixa de salvar o cartão, e
  // fatura avulsa nunca renova (29/10).
  {
    value: 'credit_card',
    label: 'Cartão de crédito',
    hint: 'Aprovação em instantes',
    icon: CreditCard,
  },
  { value: 'pix', label: 'Pix', hint: 'Aprovação na hora', icon: QrCode },
];

/**
 * Leitura da cobrança — MESMA configuração pros dois observadores (o que escolhe
 * o visual e o visual padrão), senão o 2º refaz a busca ao montar:
 * - `retryOnMount: false`: com a cobrança em erro (link quebrado), o visual
 *   padrão montava, rebuscava, a consulta voltava a "carregando", o escolhedor
 *   desmontava o padrão… laço infinito de requisições (pego em teste, 20/10).
 * - `staleTime`: com a cobrança lida, o visual padrão não busca de novo.
 */
function chargeDetailQuery(chargeId: string | undefined) {
  return {
    queryKey: ['charge-checkout', chargeId],
    queryFn: () => fetchChargeCheckout(chargeId as string),
    enabled: Boolean(chargeId),
    retry: false,
    retryOnMount: false,
    staleTime: 60_000,
  };
}

/**
 * Escolhe o visual pela cobrança (2026-10-20): assinatura do Reservei → tela do
 * Reservei; o resto (outros SaaS, avulsa, pacote) → visual padrão abaixo.
 * Mesma chave de consulta do visual padrão: a cobrança é lida uma vez só.
 */
export function ChargeCheckoutPage(): JSX.Element {
  const { chargeId } = useParams<{ chargeId: string }>();
  const detailQ = useQuery(chargeDetailQuery(chargeId));

  // Carregando: nem um visual nem outro — evita piscar o padrão pra quem é do Reservei.
  if (detailQ.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" role="status" aria-label="Carregando a fatura">
        <Loader2 className="animate-spin text-black/40" size={28} aria-hidden="true" />
      </div>
    );
  }
  if (chargeId && detailQ.data?.saas?.slug === 'reservei') {
    return <ReserveiChargeCheckout chargeId={chargeId} detail={detailQ.data} />;
  }
  return <DefaultChargeCheckout />;
}

function DefaultChargeCheckout(): JSX.Element {
  const { chargeId } = useParams<{ chargeId: string }>();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('review');
  // Cartão pré-selecionado: é o padrão do negócio (o único meio que renova
  // sozinho). Pix continua a um clique de distância, como alternativa.
  const [method, setMethod] = useState<Method>('credit_card');
  // Marcada por padrão (decisão do Wesley, 29/10) — ver ReserveiChargeCheckout.
  const [saveCard, setSaveCard] = useState(true);
  const [result, setResult] = useState<ChargePayResult | null>(null);
  const [card, setCard] = useState({
    number: '',
    holder_name: '',
    expiry_month: '',
    expiry_year: '',
    ccv: '',
    postal_code: '',
    address_number: '',
  });

  const detailQ = useQuery(chargeDetailQuery(chargeId));

  const payMutation = useMutation({
    mutationFn: () =>
      payCharge(chargeId as string, {
        payment_method: method,
        credit_card:
          method === 'credit_card'
            ? {
                number: onlyDigits(card.number),
                holder_name: card.holder_name,
                expiry_month: card.expiry_month,
                expiry_year: card.expiry_year,
                ccv: card.ccv,
                postal_code: onlyDigits(card.postal_code) || undefined,
                address_number: card.address_number || undefined,
              }
            : undefined,
        ...(method === 'credit_card' && detailQ.data?.card_save_offer ? { save_card: saveCard } : {}),
      }),
    onSuccess: (r) => {
      setResult(r);
      setStep('result');
      void qc.invalidateQueries({ queryKey: ['charge-status', chargeId] });
    },
    // mapAsaasError e não um texto fixo: "resultado desconhecido" tem que dizer
    // "não pague de novo" — "tente de novo" cobrava duas vezes (29/10).
    onError: (err) => toast.error(mapAsaasError(err)),
  });

  const paidAlready = detailQ.data?.paid ?? false;
  const statusQ = useQuery({
    queryKey: ['charge-status', chargeId],
    queryFn: () => fetchChargeCheckoutStatus(chargeId as string),
    enabled: Boolean(chargeId) && step === 'result' && !paidAlready,
    refetchInterval: (q) => (q.state.data?.paid ? false : 5_000),
    retry: false,
  });
  const paid = paidAlready || (statusQ.data?.paid ?? false);

  if (detailQ.isLoading) {
    return (
      <CheckoutShell title="PAGAMENTO">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin text-black/55" size={32} />
        </div>
      </CheckoutShell>
    );
  }

  if (detailQ.isError || !detailQ.data) {
    return (
      <CheckoutShell title="PAGAMENTO">
        <CheckoutErrorBox message="Não conseguimos carregar essa cobrança. Confira o link recebido." />
      </CheckoutShell>
    );
  }

  const d = detailQ.data;

  // Cobrança já paga (abriu link de cobrança quitada).
  if (d.paid) {
    return (
      <CheckoutShell title="PAGAMENTO">
        <PaidBanner descricao={d.descricao} valor={d.valor} />
      </CheckoutShell>
    );
  }

  const cardValid =
    method !== 'credit_card' ||
    (onlyDigits(card.number).length >= 13 &&
      card.holder_name.trim().length >= 2 &&
      /^(0[1-9]|1[0-2])$/.test(card.expiry_month) &&
      /^\d{4}$/.test(card.expiry_year) &&
      card.ccv.length >= 3);

  return (
    <CheckoutShell title="PAGAMENTO">
      <ProgressSteps step={step} />

      {/* Mesma proporção do checkout de compra: conteúdo fluido + resumo de
          largura fixa, com respiro largo entre as colunas. */}
      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-16">
        {/* Coluna principal */}
        <div>
          {step === 'review' && (
            <CheckoutSection title="Confira a cobrança" icon={Check}>
              <div className="flex flex-col gap-3 text-[13px]">
                <Row label="Cliente" value={d.cliente_nome} />
                <Row label="Descrição" value={d.descricao} />
                <Row label="Valor" value={formatBRL(d.valor)} strong />
              </div>
              <button
                type="button"
                onClick={() => setStep('payment')}
                className="mt-8 w-full rounded-full bg-black px-6 py-4 text-[14px] font-bold uppercase tracking-[0.1em] text-white transition-opacity hover:opacity-85 sm:w-auto sm:px-10"
              >
                Ir pro pagamento
              </button>
            </CheckoutSection>
          )}

          {step === 'payment' && (
            <CheckoutSection title="Como você quer pagar?" icon={Wallet}>
              {/* Duas opções, duas colunas. Estava `sm:grid-cols-3` de quando
                  havia boleto — deixava uma coluna vazia desde que ele saiu. */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {METHODS.map((m) => {
                  const active = method === m.value;
                  const Icon = m.icon;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setMethod(m.value)}
                      aria-pressed={active}
                      className={cn(
                        'flex items-center gap-3 rounded-[14px] border bg-white p-4 text-left transition-all',
                        active
                          ? 'border-black shadow-[0_1px_3px_rgba(0,0,0,0.08)] ring-1 ring-black'
                          : 'border-black/12 hover:border-black/30',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors',
                          active ? 'bg-black text-white' : 'bg-black/[0.05] text-black/60',
                        )}
                      >
                        <Icon size={18} strokeWidth={1.8} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-semibold text-black">{m.label}</span>
                        <span className="block text-[12px] text-black/50">{m.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {method === 'credit_card' && (
                <div className="mt-5 flex flex-col gap-3 border-t border-black/10 pt-5">
                  <CardInput
                    label="Número do cartão"
                    value={card.number}
                    onChange={(v) => setCard((c) => ({ ...c, number: maskCard(v) }))}
                    placeholder="1234 5678 9012 3456"
                  />
                  <CardInput
                    label="Nome impresso no cartão"
                    value={card.holder_name}
                    onChange={(v) => setCard((c) => ({ ...c, holder_name: v }))}
                    placeholder="JOÃO DA SILVA"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <CardInput
                      label="Mês"
                      value={card.expiry_month}
                      onChange={(v) =>
                        setCard((c) => ({ ...c, expiry_month: onlyDigits(v).slice(0, 2) }))
                      }
                      placeholder="MM"
                    />
                    <CardInput
                      label="Ano"
                      value={card.expiry_year}
                      onChange={(v) =>
                        setCard((c) => ({ ...c, expiry_year: onlyDigits(v).slice(0, 4) }))
                      }
                      placeholder="AAAA"
                    />
                    <CardInput
                      label="CVV"
                      value={card.ccv}
                      onChange={(v) => setCard((c) => ({ ...c, ccv: onlyDigits(v).slice(0, 4) }))}
                      placeholder="123"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <CardInput
                      label="CEP do titular"
                      value={card.postal_code}
                      onChange={(v) => setCard((c) => ({ ...c, postal_code: maskCep(v) }))}
                      placeholder="00000-000"
                    />
                    <CardInput
                      label="Nº do endereço"
                      value={card.address_number}
                      onChange={(v) => setCard((c) => ({ ...c, address_number: v }))}
                      placeholder="100"
                    />
                  </div>
                  {d.card_save_offer && (
                    // Mesmo traço do aceite do checkout de compra (ConsentBlock).
                    <label className="mt-1 flex min-h-[44px] cursor-pointer items-start gap-2 text-[12px] leading-snug text-slate-600">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        className="mt-[2px] h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-400"
                      />
                      <span>{cardSaveText(d.card_save_offer)}</span>
                    </label>
                  )}
                </div>
              )}

              <div className="mt-5 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setStep('review')}
                  className="text-[12px] text-black/55 hover:text-black"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  disabled={!cardValid || payMutation.isPending}
                  onClick={() => payMutation.mutate()}
                  className="inline-flex items-center gap-2 rounded-[10px] bg-black px-5 py-2.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {payMutation.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Lock size={14} strokeWidth={2} />
                  )}
                  Pagar {formatBRL(d.valor)}
                </button>
              </div>
            </CheckoutSection>
          )}

          {step === 'result' && result && <ResultSection result={result} paid={paid} />}
        </div>

        {/* Resumo — o único card da tela. Aqui a caixa trabalha: separa o valor
            que o cliente confere do formulário que ele preenche. `sticky` porque
            no passo do cartão a coluna esquerda fica mais alta que a direita, e
            o total sumia da vista bem na hora de confirmar. */}
        <aside className="lg:sticky lg:top-8">
          <div className="rounded-[18px] border border-black/[0.07] bg-white p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)]">
            <div className="mb-5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-black">
              <ShoppingBag size={13} strokeWidth={2.2} aria-hidden="true" />
              Resumo
            </div>

            <p className="text-[14px] leading-relaxed text-black/60">{d.descricao}</p>

            <div className="mt-5 flex items-baseline justify-between border-t border-black/10 pt-5">
              <span className="text-[13px] font-medium text-black/60">Total</span>
              <span className="tnum text-[28px] font-black tracking-tight text-black">
                {formatBRL(d.valor)}
              </span>
            </div>

            <ul className="mt-6 space-y-2.5 border-t border-black/[0.07] pt-5">
              {[
                'Pagamento processado pelo Asaas',
                ...(d.card_save_offer ? ['Cartão salvo, se você quiser, paga as próximas mensalidades'] : []),
                'Acesso liberado assim que confirmar',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-[12px] leading-snug text-black/60">
                  <Check size={13} strokeWidth={2.5} className="mt-[3px] shrink-0 text-teal-600" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-black/40">
            <Lock size={11} strokeWidth={2} />
            Seus dados são enviados criptografados
          </p>
        </aside>
      </div>
    </CheckoutShell>
  );
}

// =============================================================================
// Result (PIX / boleto / cartão processando) + polling
// =============================================================================

function ResultSection({ result, paid }: { result: ChargePayResult; paid: boolean }): JSX.Element {
  if (paid) {
    return (
      <CheckoutSection title="Pagamento confirmado" icon={CheckCircle2}>
        <div className="flex items-center gap-3 rounded-[12px] border border-teal-600/40 bg-teal-600/10 p-4">
          <CheckCircle2 size={20} className="text-teal-600" strokeWidth={2} />
          <div className="text-[13px] font-semibold text-teal-600">
            Tudo certo! Recebemos seu pagamento.
          </div>
        </div>
      </CheckoutSection>
    );
  }

  if (result.payment_method === 'pix') {
    return <PixResult result={result} />;
  }
  if (result.payment_method === 'boleto') {
    return <BoletoResult result={result} />;
  }
  return <CardProcessing paymentUrl={result.payment_url} />;
}

function PixResult({ result }: { result: ChargePayResult }): JSX.Element {
  if (!result.pix_qr_image || !result.pix_payload) {
    return (
      <CheckoutErrorBox
        message="Não conseguimos gerar o Pix agora. Use o link do Asaas."
        fallbackUrl={result.payment_url}
      />
    );
  }
  return (
    <CheckoutSection title="Pague com Pix" icon={QrCode}>
      <div className="flex justify-center">
        <div className="rounded-[12px] border border-black/10 bg-white p-3">
          <img
            src={`data:image/png;base64,${result.pix_qr_image}`}
            alt="QR Code Pix"
            className="h-[220px] w-[220px]"
          />
        </div>
      </div>
      <div className="mt-5">
        <CopyCodeField
          code={result.pix_payload}
          label="Ou copie o código"
          successMessage="Código Pix copiado!"
        />
      </div>
      <CheckoutWaitingFooter />
    </CheckoutSection>
  );
}

function BoletoResult({ result }: { result: ChargePayResult }): JSX.Element {
  return (
    <CheckoutSection title="Pague com Boleto" icon={Receipt}>
      {result.bank_slip_line ? (
        <CopyCodeField code={result.bank_slip_line} successMessage="Linha digitável copiada!" />
      ) : (
        <div className="text-[12px] text-black/55">Gerando boleto…</div>
      )}
      {result.bank_slip_url && (
        <a
          href={result.bank_slip_url}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-2 rounded-[10px] border border-black/10 bg-black/[0.035] px-3 py-2 text-[12px] font-medium text-black/70 hover:border-black/25"
        >
          <Building2 size={12} strokeWidth={2} />
          Abrir boleto
        </a>
      )}
      <CheckoutWaitingFooter />
    </CheckoutSection>
  );
}

function CardProcessing({ paymentUrl }: { paymentUrl: string | null }): JSX.Element {
  return (
    <CheckoutSection title="Processando cartão…" icon={CreditCard}>
      <p className="text-[13px] text-black/55">
        Enviamos sua cobrança pra autorização do banco. Em geral é instantâneo — assim que
        confirmar, esta tela atualiza.
      </p>
      {paymentUrl && (
        <a
          href={paymentUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex text-[12px] font-medium text-teal-700 hover:underline"
        >
          Ver detalhes da cobrança
        </a>
      )}
      <CheckoutWaitingFooter />
    </CheckoutSection>
  );
}

// =============================================================================
// UI helpers
// =============================================================================

function ProgressSteps({ step }: { step: Step }): JSX.Element {
  const order: Step[] = ['review', 'payment', 'result'];
  const idx = order.indexOf(step);
  const labels: Record<Step, string> = {
    review: 'Revisar',
    payment: 'Pagamento',
    result: 'Pronto',
  };
  return (
    <div className="mb-6 flex items-center gap-3">
      {order.map((s, i) => {
        const done = i < idx;
        const current = i === idx;
        return (
          <div key={s} className="flex items-center gap-2">
            <div
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full border-2 text-[12px] font-semibold transition-colors',
                done || current ? 'border-black bg-black text-white' : 'border-black/15 text-black/35',
              )}
            >
              {done ? <Check size={14} strokeWidth={2.5} /> : i + 1}
            </div>
            <span className={cn('text-[12px]', current ? 'font-semibold text-black' : 'text-black/35')}>
              {labels[s]}
            </span>
            {i < order.length - 1 && <div className="h-px w-6 bg-black/15" />}
          </div>
        );
      })}
    </div>
  );
}

function Row({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-black/45">{label}</span>
      <span
        className={cn(
          'text-right',
          strong ? 'text-[15px] font-semibold text-black tnum' : 'text-black',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function PaidBanner({ descricao, valor }: { descricao: string; valor: string }): JSX.Element {
  return (
    <div className="mx-auto max-w-[460px] rounded-[16px] border border-teal-600/40 bg-teal-600/10 p-8 text-center">
      <CheckCircle2 size={32} strokeWidth={1.8} className="mx-auto text-teal-600" />
      <h2 className="mt-3 text-[16px] font-semibold text-black">Cobrança já paga</h2>
      <p className="mt-1 text-[13px] text-black/55">
        {descricao} — {formatBRL(valor)}
      </p>
    </div>
  );
}

